// Zod contracts for Ticket Core (C10, M3): the commands every channel sends, their results,
// the moves a caller may make, and the event payloads deliverers read (architecture 4.6, 4.9).
//
// COMMANDS. Every ticket change is one command to TicketCore.execute (REQ-21.3). Every command
// carries `ticketId` and, except on the triage, automation and linear channels, `expectedRevision`: the
// revision the caller last read (REQ-11.7). The HTTP routes take the same objects without `ticketId`
// (it comes from the path) and with `expectedRevision` required. Every staff command can carry a `reason`
// (module-3 Section 3), stored on its change (ticket_changes.reason): required by decline and close_without_fix
// (a missing one is 400 validation_failed at parse, decision D2) and by any move whose transition requires one
// (422 reason_required), optional everywhere else.
//
// EVENT PAYLOADS (events.payload, payload_version 1). They hold IDs and non-personal values only:
// reference, title, app, stage and status codes and names, fix note, links. People's names are never
// stored in a payload (anonymisation doesn't rewrite payloads, REQ-19.2): deliverers resolve names
// from the principal IDs. Reporter-written text (reopen notes, replies) is never in a payload either;
// deliverers read it from the change or comment the event points to.
import { z } from 'zod';
import { CODE_LISTS } from '../constants/code-lists.js';
import { LINEAR_TARGETS } from '../constants/linear.js';
import { StageCodeSchema, StatusSystemCodeSchema, WorkflowCodeSchema } from './workflow.js';

const Uuid = z.uuid();
const Revision = z.number().int().min(1);
const Reason = z.string().trim().min(1).max(1000);
const Note = z.string().trim().max(1000);
const CommentBody = z.string().trim().min(1).max(20000);
const SHORT_TEXT_MAX = 1000;
const LONG_TEXT_MAX = 20000;

export const DescriptionSectionSchema = z.enum(CODE_LISTS.description_section);
/** A section set to null is removed (a section the input doesn't support has no row, REQ-6.1). */
export const DescriptionSectionsSchema = z.partialRecord(
  DescriptionSectionSchema,
  z.string().trim().max(LONG_TEXT_MAX).nullable(),
);
export const TicketTypeCodeSchema = z.enum(['bug', 'feature_request']);
export const ChannelSchema = z.enum(CODE_LISTS.channel);
export const FlagTypeSchema = z.enum(CODE_LISTS.flag_type);

const base = { ticketId: Uuid, expectedRevision: Revision.optional() };

// ---- Triage (AI triage principal, channel triage; triage_fallback also automation) ------------------

const TRIAGE_REASON_FIELDS = [
  'title',
  'description',
  'type',
  'suggested_priority',
  'responsible_owner',
  'app',
  'assignee',
  'risk_result',
] as const;

export const ApplyTriageCommandSchema = z.object({
  type: z.literal('apply_triage'),
  ...base,
  title: z.string().trim().min(1).max(SHORT_TEXT_MAX).optional(),
  descriptionSections: DescriptionSectionsSchema.optional(),
  ticketType: TicketTypeCodeSchema,
  /** A priorities.code. */
  suggestedPriority: z.string().min(1).max(100).optional(),
  /** Route to another app (REQ-6.6). Default: stays where it is. */
  appId: Uuid.optional(),
  /** An Editor or Admin, or 'fallback' for the app's fallback owner (or the Admins when there's none). */
  responsiblePrincipalId: z.union([Uuid, z.literal('fallback')]).optional(),
  /**
   * Routing's best-matching Editor for an auto-accepted bug (REQ-6.6): the same command then assigns
   * it, so it goes on to In progress (poc CT-007, module-3 Section 7.4).
   */
  assigneePrincipalId: Uuid.optional(),
  riskResult: z.enum(CODE_LISTS.risk_result).optional(),
  /** The risk_assessments row triage wrote for this decision (recorded on the change). */
  riskAssessmentId: Uuid.optional(),
  duplicateCandidates: z
    .array(z.object({ ticketId: Uuid, matchStrength: z.number().min(0).max(1), reason: Reason }))
    .max(50)
    .optional(),
  /** The LLM's reason for each field it set (REQ-6.8), kept on the history row. */
  reasons: z.partialRecord(z.enum(TRIAGE_REASON_FIELDS), Reason).optional(),
});

export const TriageFallbackCommandSchema = z.object({
  type: z.literal('triage_fallback'),
  ...base,
  /**
   * The version the reporter sent from "What we'll send" (poc CT-020): the AI's, with the reporter's edits winning,
   * or only their edits when the AI couldn't prepare one. Missing for every other report (it stays as it is).
   */
  title: z.string().trim().min(1).max(SHORT_TEXT_MAX).optional(),
  descriptionSections: DescriptionSectionsSchema.optional(),
});

export const LinkDuplicateReportCommandSchema = z.object({
  type: z.literal('link_duplicate_report'),
  ...base,
  reportId: Uuid,
  matchStrength: z.number().min(0).max(1).optional(),
  reason: Reason.optional(),
});

// ---- Owners: duplicates, fields, decisions --------------------------------------------------------

export const MergeCommandSchema = z.object({
  type: z.literal('merge'),
  ...base,
  /** The open ticket to merge into: a reference (FB-1042) or a ticket ID. */
  intoTicket: z.string().trim().min(1).max(100),
  reason: Reason.optional(),
});

export const UnlinkReportCommandSchema = z.object({
  type: z.literal('unlink_report'),
  ...base,
  /** The report's ID or its public ID. */
  reportId: z.string().trim().min(1).max(100),
  reason: Reason.optional(),
});

export const SetFieldsCommandSchema = z.object({
  type: z.literal('set_fields'),
  ...base,
  title: z.string().trim().min(1).max(SHORT_TEXT_MAX).optional(),
  descriptionSections: DescriptionSectionsSchema.optional(),
  ticketType: TicketTypeCodeSchema.optional(),
  responsiblePrincipalId: Uuid.optional(),
  appId: Uuid.optional(),
  /** Optional reason for the correction (REQ-6.8). */
  reason: Reason.optional(),
});

export const SetPriorityCommandSchema = z.object({
  type: z.literal('set_priority'),
  ...base,
  /** A priorities.code, or null to clear the owner's priority (the suggestion stays). */
  priority: z.string().min(1).max(100).nullable(),
  reason: Reason.optional(),
});

export const AcceptCommandSchema = z.object({
  type: z.literal('accept'),
  ...base,
  /** Assign it as it's accepted: the same command then moves it on to In progress (module-3 Section 8.5). */
  assigneePrincipalId: Uuid.optional(),
  reason: Reason.optional(),
});

export const DeclineCommandSchema = z.object({
  type: z.literal('decline'),
  ...base,
  /** A Not actioned outcome status of the ticket's version. Default: the stage default (Declined). */
  outcomeStatusId: Uuid.optional(),
  reason: Reason,
});

export const CloseWithoutFixCommandSchema = z.object({
  type: z.literal('close_without_fix'),
  ...base,
  outcomeStatusId: Uuid.optional(),
  reason: Reason,
});

// ---- Assignment: assigning is the hand-off (poc CT-007) ---------------------------------------------------

/** Ready -> In progress with the assignee, or a new assignee while In progress. The actor may assign themselves. */
export const AssignCommandSchema = z.object({
  type: z.literal('assign'),
  ...base,
  assigneePrincipalId: Uuid,
  reason: Reason.optional(),
});
export const ReassignCommandSchema = z.object({
  type: z.literal('reassign'),
  ...base,
  assigneePrincipalId: Uuid,
  reason: Reason.optional(),
});
/** In progress (or the legacy Assigned) -> Ready (Accepted), with no assignee. */
export const UnassignCommandSchema = z.object({ type: z.literal('unassign'), ...base, reason: Reason.optional() });

// ---- Moves --------------------------------------------------------------------------------------------

export const MoveCommandSchema = z.object({
  type: z.literal('move'),
  ...base,
  toStatusId: Uuid,
  reason: Reason.optional(),
});

export const MoveBackCommandSchema = z.object({
  type: z.literal('move_back'),
  ...base,
  /** A Review status of the ticket's version. Default: the Review stage default (Needs review). */
  toStatusId: Uuid.optional(),
  reason: Reason.optional(),
});

export const MarkFixedCommandSchema = z.object({
  type: z.literal('mark_fixed'),
  ...base,
  /** Reporters read it (the release note for a feature request); an app calling as itself may not send one. */
  fixNote: Note.optional(),
  /** Staff only, on the change. */
  reason: Reason.optional(),
});

export const MoveFixedBackCommandSchema = z.object({
  type: z.literal('move_fixed_back'),
  ...base,
  reason: Reason.optional(),
});

// ---- Comments and flags -------------------------------------------------------------------------------

export const CommentCommandSchema = z.object({
  type: z.literal('comment'),
  ...base,
  body: CommentBody,
  /** Internal unless the author chooses otherwise (REQ-8.6). */
  visibility: z.enum(CODE_LISTS.comment_visibility).optional(),
  /** People with a role in the ticket's app (principal IDs). */
  mentions: z.array(Uuid).max(20).optional(),
  /** Files already stored through C11 (POST /api/tickets/:ref/attachments), now linked to the comment. */
  attachmentIds: z.array(Uuid).max(10).optional(),
  inReplyToCommentId: Uuid.optional(),
  reason: Reason.optional(),
});

export const SetFlagCommandSchema = z.object({
  type: z.literal('set_flag'),
  ...base,
  flag: z.literal('awaiting_reporter'),
  reason: Reason.optional(),
});
export const ClearFlagCommandSchema = z.object({
  type: z.literal('clear_flag'),
  ...base,
  flag: z.literal('awaiting_reporter'),
  reason: Reason.optional(),
});

// ---- Reporters (channel reporter_pages or drawer; reportId comes from the route) -----------------------

export const ConfirmFixCommandSchema = z.object({ type: z.literal('confirm_fix'), ...base, reportId: Uuid });
export const ReopenCommandSchema = z.object({ type: z.literal('reopen'), ...base, reportId: Uuid, reason: Reason });
export const WithdrawReportCommandSchema = z.object({ type: z.literal('withdraw_report'), ...base, reportId: Uuid });
export const ReporterReplyCommandSchema = z.object({
  type: z.literal('reporter_reply'),
  ...base,
  reportId: Uuid,
  body: CommentBody,
  attachmentIds: z.array(Uuid).max(10).optional(),
  inReplyToCommentId: Uuid.optional(),
});

// ---- The tool's own automation (system principal, channel automation) ---------------------------------

export const AutoCloseCommandSchema = z.object({ type: z.literal('auto_close'), ...base });
export const ResponsibilityPassedCommandSchema = z.object({
  type: z.literal('responsibility_passed'),
  ...base,
  /** An owner of the app (normally its fallback owner) or the Platform admins principal. */
  toPrincipalId: Uuid,
});

// ---- Linear (the System on channel `linear`; poc CT-009, module-14 Sections 3 to 5) -----------------------

const LinearIdentifier = z.string().trim().min(1).max(100);

/** The `linear` deliverer created the ticket's Linear issue: a `handed_off` history entry, no status change. */
export const RecordHandoffCommandSchema = z.object({
  type: z.literal('record_handoff'),
  ...base,
  /** FBS-12 */
  identifier: LinearIdentifier,
  url: z.url().max(SHORT_TEXT_MAX),
});

/**
 * The sync mirrors the ticket's Linear issue (module-14 Section 4, `applyLinearIssue`). `target` comes from the
 * issue's state type (constants/linear `linearTargetOf`); Ticket Core picks the status in the ticket's own
 * workflow version and makes ONE change through that version's transition (no transition: refused):
 *   ready        Ready / `assigned` with the assignee (`accepted` when there's none)
 *   in_progress  In progress (stage default)
 *   completed    bug: `fixed` with no fix note; feature request: `shipped`
 *   canceled     Not actioned stage default, reason "Closed without a fix by the team."
 * Reporters can read the fix note and the reason, so neither names Linear: the identifier and Linear's state
 * name stay in the event details (a cancel adds `linearReason`), which only staff surfaces read; the staff
 * history words them as "Done in Linear: FBS-12 (Done)" or "Canceled in Linear (FBS-12: Duplicate)". Nothing is
 * recorded when the ticket is already there with that assignee.
 */
export const ApplyLinearStateCommandSchema = z.object({
  type: z.literal('apply_linear_state'),
  ...base,
  /**
   * Null keeps the ticket's status: the issue's state hasn't changed since the last sync, only its assignee
   * (the sync never re-applies a state it already applied, so the tool's own reporter-loop moves stand).
   */
  target: z.enum(LINEAR_TARGETS).nullable(),
  /** The Editor or Admin who has the issue in Linear (matched by email). Null keeps the ticket's assignee. */
  assigneePrincipalId: Uuid.nullable(),
  /** FBS-12, for the staff history (event details). */
  identifier: LinearIdentifier,
  /** Linear's own status name, for example "Duplicate", for the staff history (event details). */
  stateName: z.string().trim().min(1).max(SHORT_TEXT_MAX),
});

export const TicketCommandSchema = z.discriminatedUnion('type', [
  ApplyTriageCommandSchema,
  TriageFallbackCommandSchema,
  LinkDuplicateReportCommandSchema,
  MergeCommandSchema,
  UnlinkReportCommandSchema,
  SetFieldsCommandSchema,
  SetPriorityCommandSchema,
  AcceptCommandSchema,
  DeclineCommandSchema,
  CloseWithoutFixCommandSchema,
  AssignCommandSchema,
  ReassignCommandSchema,
  UnassignCommandSchema,
  MoveCommandSchema,
  MoveBackCommandSchema,
  MarkFixedCommandSchema,
  MoveFixedBackCommandSchema,
  CommentCommandSchema,
  SetFlagCommandSchema,
  ClearFlagCommandSchema,
  ConfirmFixCommandSchema,
  ReopenCommandSchema,
  WithdrawReportCommandSchema,
  ReporterReplyCommandSchema,
  AutoCloseCommandSchema,
  ResponsibilityPassedCommandSchema,
  RecordHandoffCommandSchema,
  ApplyLinearStateCommandSchema,
]);
export type TicketCommand = z.infer<typeof TicketCommandSchema>;
export type TicketCommandType = TicketCommand['type'];
export type CommandOf<T extends TicketCommandType> = Extract<TicketCommand, { type: T }>;
export const TICKET_COMMAND_TYPES = TicketCommandSchema.options.map((o) => o.shape.type.value) as TicketCommandType[];

// ---- HTTP bodies ----------------------------------------------------------------------------------------

/** POST /api/tickets/:ref/commands: staff pages and the dashboard. */
export const StaffCommandBodySchema = z.discriminatedUnion('type', [
  MergeCommandSchema.omit({ ticketId: true }).extend({ expectedRevision: Revision }),
  UnlinkReportCommandSchema.omit({ ticketId: true }).extend({ expectedRevision: Revision }),
  SetFieldsCommandSchema.omit({ ticketId: true }).extend({ expectedRevision: Revision }),
  SetPriorityCommandSchema.omit({ ticketId: true }).extend({ expectedRevision: Revision }),
  AcceptCommandSchema.omit({ ticketId: true }).extend({ expectedRevision: Revision }),
  DeclineCommandSchema.omit({ ticketId: true }).extend({ expectedRevision: Revision }),
  CloseWithoutFixCommandSchema.omit({ ticketId: true }).extend({ expectedRevision: Revision }),
  AssignCommandSchema.omit({ ticketId: true }).extend({ expectedRevision: Revision }),
  ReassignCommandSchema.omit({ ticketId: true }).extend({ expectedRevision: Revision }),
  UnassignCommandSchema.omit({ ticketId: true }).extend({ expectedRevision: Revision }),
  MoveCommandSchema.omit({ ticketId: true }).extend({ expectedRevision: Revision }),
  MoveBackCommandSchema.omit({ ticketId: true }).extend({ expectedRevision: Revision }),
  MarkFixedCommandSchema.omit({ ticketId: true }).extend({ expectedRevision: Revision }),
  MoveFixedBackCommandSchema.omit({ ticketId: true }).extend({ expectedRevision: Revision }),
  CommentCommandSchema.omit({ ticketId: true }).extend({ expectedRevision: Revision }),
  SetFlagCommandSchema.omit({ ticketId: true }).extend({ expectedRevision: Revision }),
  ClearFlagCommandSchema.omit({ ticketId: true }).extend({ expectedRevision: Revision }),
]);
export type StaffCommandBody = z.infer<typeof StaffCommandBodySchema>;

/** POST /api/my-requests/:publicId/commands: the reporter's own report (reportId from the path). */
export const ReporterCommandBodySchema = z.discriminatedUnion('type', [
  ConfirmFixCommandSchema.omit({ ticketId: true, reportId: true }).extend({ expectedRevision: Revision }),
  ReopenCommandSchema.omit({ ticketId: true, reportId: true }).extend({ expectedRevision: Revision }),
  WithdrawReportCommandSchema.omit({ ticketId: true, reportId: true }).extend({ expectedRevision: Revision }),
  ReporterReplyCommandSchema.omit({ ticketId: true, reportId: true }).extend({ expectedRevision: Revision }),
]);
export type ReporterCommandBody = z.infer<typeof ReporterCommandBodySchema>;

export const TicketRefParamsSchema = z.object({ ref: z.string().trim().min(1).max(100) });
export const ReportPublicIdParamsSchema = z.object({ publicId: z.string().trim().min(1).max(100) });

// ---- Results ----------------------------------------------------------------------------------------------

export const CommandResultSchema = z.object({
  ticketId: Uuid,
  reference: z.string(),
  revision: z.number().int(),
  changeId: Uuid.nullable(),
  eventId: Uuid.nullable(),
});
export type CommandResult = z.infer<typeof CommandResultSchema>;

/** One move the caller may make now (REQ-11.7): the detail page's buttons and MCP's get_ticket_workflow. */
export const AllowedMoveSchema = z.object({
  transitionId: Uuid,
  toStatus: z.object({
    id: Uuid,
    name: z.string(),
    stage: StageCodeSchema,
    systemCode: StatusSystemCodeSchema.nullable(),
  }),
  requiresReason: z.boolean(),
  /** The Ticket Core command that makes this move. */
  command: z.enum(TICKET_COMMAND_TYPES as [TicketCommandType, ...TicketCommandType[]]),
  /**
   * `set_fields` moves only: the type the move sets. The pages send this value as `ticketType` and never
   * assume one (poc CT-004, module-3 Section 6.7).
   */
  ticketType: TicketTypeCodeSchema.optional(),
});
export type AllowedMove = z.infer<typeof AllowedMoveSchema>;

// ---- Event payloads (payload_version 1) -------------------------------------------------------------------

export const TICKET_EVENT_PAYLOAD_VERSION = 1;

const StageRefSchema = z.object({ code: StageCodeSchema, name: z.string() });
const StatusPayloadSchema = z.object({ id: Uuid, systemCode: StatusSystemCodeSchema.nullable(), name: z.string() });

export const EventActorSchema = z.object({
  principalId: Uuid,
  kind: z.enum(CODE_LISTS.principal_kind),
  channel: ChannelSchema,
  /** Set when an MCP client such as Claude acted for the person: "Claude on behalf of <person>" (REQ-11.4). */
  mcpClientId: Uuid.nullable(),
  mcpClientName: z.string().nullable(),
});

/** A report of the ticket, for reporter audiences: the deliverer links each reporter to their own status view. */
export const EventReportSchema = z.object({
  publicId: z.string(),
  reporterPrincipalId: Uuid,
  /** `/my-requests/<publicId>` */
  link: z.string(),
});

export const TicketEventBaseSchema = z.object({
  payloadVersion: z.literal(TICKET_EVENT_PAYLOAD_VERSION),
  ticket: z.object({
    id: Uuid,
    reference: z.string(),
    title: z.string().nullable(),
    appId: Uuid,
    appName: z.string(),
    revision: z.number().int(),
    workflowVersionNumber: z.number().int().nullable(),
    /**
     * The workflow of that version (poc CT-004): deliverers word a feature request's stage changes from it,
     * never from a status name. Absent on events recorded before CT-004 (all in the bug workflow).
     */
    workflowCode: WorkflowCodeSchema.optional(),
  }),
  stage: StageRefSchema,
  status: StatusPayloadSchema,
  /** Before the change, when the status changed. */
  previous: z.object({ stage: StageRefSchema, status: StatusPayloadSchema }).nullable(),
  stageChanged: z.boolean(),
  actor: EventActorSchema,
  links: z.object({ ticket: z.string() }),
  /** The ticket's non-withdrawn reports (and, for unlink and merge, the reports that moved). */
  reports: z.array(EventReportSchema),
});
export type TicketEventBase = z.infer<typeof TicketEventBaseSchema>;

/**
 * A ticket moved to the other type's workflow (poc CT-004, module-3 Section 6.2): set on the `triaged` and
 * `fields_changed` events that made the switch. The event's `ticket.workflowVersionNumber` is the new one.
 */
const WorkflowRefSchema = z.object({ workflowCode: WorkflowCodeSchema, versionNumber: z.number().int().nullable() });
export const WorkflowSwitchSchema = z.object({ from: WorkflowRefSchema, to: WorkflowRefSchema });
export type WorkflowSwitch = z.infer<typeof WorkflowSwitchSchema>;

/** Kind-specific details, keyed by event type. Event types not listed carry `{}`. */
export const TICKET_EVENT_DETAILS = {
  'ticket.created': z.object({ reportPublicId: z.string(), acknowledged: z.boolean() }),
  'ticket.triaged': z.object({
    /**
     * `feature_request`: sent to the feature-request workflow's backlog (poc CT-004). `accepted` with an assignee
     * is followed by the same command's `ticket.assigned` (poc CT-007). `assigned` is legacy: before CT-007,
     * auto-accepted straight into the Assigned status.
     */
    outcome: z.enum(['needs_review', 'accepted', 'assigned', 'feature_request', 'fallback']),
    acknowledgement: z.boolean(),
    riskResult: z.enum(CODE_LISTS.risk_result).nullable(),
    riskAssessmentId: Uuid.nullable(),
    assigneePrincipalId: Uuid.nullable(),
    /** poc CT-009: why routing's pick wasn't assigned (no Linear account); null otherwise. Absent before CT-009. */
    assigneeDropped: z.string().nullable().optional(),
    responsiblePrincipalId: Uuid.nullable(),
    notTriagedByAi: z.boolean(),
    workflowSwitch: WorkflowSwitchSchema.optional(),
  }),
  'ticket.fields_changed': z.object({
    fields: z.array(z.string()),
    isAiCorrection: z.boolean(),
    workflowSwitch: WorkflowSwitchSchema.optional(),
  }),
  'ticket.moved': z.object({
    command: z.string(),
    /**
     * The decline reason, only when reporters may see it (reporters.see_decline_reason, OD-8) and a person gave it:
     * never an app's (poc CT-016).
     */
    declineReason: z.string().nullable(),
  }),
  /**
   * `command`: the command that made the change (assign, reassign, accept, apply_triage, apply_linear_state,
   * legacy_assignment_upgrade; unassign, member_left). Absent on events recorded before it was stored.
   */
  'ticket.assigned': z.object({
    command: z.string().optional(),
    assigneePrincipalId: Uuid,
    previousAssigneePrincipalId: Uuid.nullable(),
  }),
  'ticket.unassigned': z.object({ command: z.string().optional(), previousAssigneePrincipalId: Uuid.nullable() }),
  'ticket.report_linked': z.object({
    reportPublicIds: z.array(z.string()),
    linkKind: z.enum(CODE_LISTS.report_link_kind),
    /** Set on the ticket merged into: the ticket whose reports it gained. */
    mergedFromTicketId: Uuid.nullable(),
  }),
  'ticket.report_unlinked': z.object({ reportPublicId: z.string(), newTicketId: Uuid, newTicketReference: z.string() }),
  'ticket.merged': z.object({ intoTicketId: Uuid, intoTicketReference: z.string(), movedReportCount: z.number() }),
  'ticket.comment_added': z.object({
    commentId: Uuid,
    kind: z.enum(CODE_LISTS.comment_kind),
    visibility: z.enum(CODE_LISTS.comment_visibility),
    reportPublicId: z.string().nullable(),
  }),
  'ticket.flag_set': z.object({ flag: FlagTypeSchema }),
  'ticket.flag_cleared': z.object({ flag: FlagTypeSchema }),
  'ticket.marked_fixed': z.object({ fixNote: z.string().nullable(), fixRecordId: Uuid }),
  'ticket.confirmed': z.object({ reportPublicId: z.string() }),
  'ticket.reopened': z.object({ reportPublicId: z.string(), reopenCount: z.number().int() }),
  'ticket.report_withdrawn': z.object({ reportPublicId: z.string(), ticketWithdrawn: z.boolean() }),
  'ticket.auto_closed': z.object({}),
  /** poc CT-009: the ticket's Linear issue was created (`record_handoff`). */
  'ticket.handed_off': z.object({ identifier: z.string(), url: z.string() }),
  'ticket.responsibility_passed': z.object({
    toPrincipalId: Uuid,
    fromPrincipalId: Uuid.nullable(),
    toPlatformAdmins: z.boolean(),
  }),
} as const;
export type TicketEventDetails = {
  [K in keyof typeof TICKET_EVENT_DETAILS]: z.infer<(typeof TICKET_EVENT_DETAILS)[K]>;
};

/** The full payload schema of one ticket event type (`ticket.<change_kind>`). */
export function ticketEventPayloadSchema(eventType: string) {
  const details = (TICKET_EVENT_DETAILS as Record<string, z.ZodObject>)[eventType] ?? z.object({});
  return TicketEventBaseSchema.extend({ details });
}
export type TicketEventPayload = TicketEventBase & { details: Record<string, unknown> };
